import { queryOptions, useQuery } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import type { DaghfalClient } from '../api/client';
import type { ClientLimits } from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { storage } from '../storage';
import { ErrorState, LoadingState } from '../ui/states';

const LimitsContext = createContext<ClientLimits | null>(null);

/**
 * Where the last limits the API sent are kept, so an app opened offline still knows them. The version moves
 * whenever the shape gains a field a screen reads without a fallback (v2: the watermark's opacity; v3: the
 * photo editor), so a copy kept by an older build is never handed to a newer one.
 */
const STORAGE_KEY = 'daghfal.limits.v3';

/**
 * The configured limits: upload sizes, the voice-note length, items per message, page sizes, polling
 * intervals and the offline queue's attempts (`GET /config/limits`, Q48).
 *
 * Every number the API enforces comes from here rather than from a constant in the app, so changing one is a
 * restart of the API and not a new build. Available anywhere under `RequireSession`, which loads them before
 * it renders anything; a component rendered outside it throws rather than guess.
 */
export function useLimits(): ClientLimits {
  const limits = useContext(LimitsContext);
  if (!limits) throw new Error('useLimits was called outside <RequireSession>, which loads the limits.');
  return limits;
}

/** Supplies limits directly. For tests, and for anything rendered outside the signed-in tree. */
export function LimitsProvider({ value, children }: { value: ClientLimits; children: ReactNode }) {
  return <LimitsContext.Provider value={value}>{children}</LimitsContext.Provider>;
}

/**
 * Loads the limits, then renders its children with them.
 *
 * The last copy the API sent is kept and shown at once, and refreshed behind it: the staff app is opened
 * offline in a classroom with no signal, and its queue needs to know when to give up on an entry before the
 * network is back. Only an app that has never reached the API waits, and it has had to reach it to sign in.
 */
export function LimitsGate({ children }: { children: ReactNode }) {
  const query = useQuery(limitsQuery(useApi()));

  if (query.data) return <LimitsProvider value={query.data}>{children}</LimitsProvider>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return <LoadingState />;
}

// Storage blocked or the value unreadable: the gate fetches them instead.
const readStored = () => storage.readJson<ClientLimits>(STORAGE_KEY);

// A private window or a full quota loses nothing but the offline copy.
const store = (limits: ClientLimits) => storage.writeJson(STORAGE_KEY, limits);

/**
 * The limits query, shared by `LimitsGate` and the session, which starts it beside `/auth/me` so a first
 * sign-in does not wait for one and then the other.
 */
export function limitsQuery(api: DaghfalClient) {
  return queryOptions({
    queryKey: ['config', 'limits'],
    queryFn: async () => {
      const limits = await api.limits();
      store(limits);
      return limits;
    },
    initialData: readStored,
    // Treated as stale from the start, so the stored copy is replaced as soon as the API answers.
    initialDataUpdatedAt: 0,
    // They change only when the API restarts with new configuration.
    staleTime: 30 * 60_000,
  });
}
